// EchoCodesWebExportButton.js - Web-only export button for generating text file with keystroke instructions

import React, { useState, useCallback } from 'react';
import './EchoCodesMacroButton.css'; // Reuse existing styles
import { getCachedCodeMap } from './echo-codes/echoCodesLoader';
import { getModifiersForEnumSet } from './echo-codes/modifier-enums.ts';
import { sortCodesByPriority } from './echo-codes/codeOrderingConfig';

/**
 * Export button that copies keystroke instructions to clipboard
 * Only visible in web browser (non-Electron) environment
 *
 * Format rules:
 * - No modifier/value: code → [ENTER] → [CLICK]
 * - Modifier only: code → [DOWN] (×N) → [ENTER] → [CLICK]
 * - Value (ignores modifier): code → [ENTER] → value → [ENTER] → [CLICK]
 */
const EchoCodesWebExportButton = ({ codes }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // Check if running in web browser (not Electron)
  const isWeb = window.isElectron !== true || window.electronAPI === undefined;

  // Flatten all codes into a single array with category info
  const getAllCodesFlat = useCallback(() => {
    const flatCodes = [];

    // Order of categories to process (same as macro button)
    const categoryOrder = [
      'vg', 'vd', 'oreillettes', 'valve_mitrale', 'valve_tricuspide',
      'valve_aortique', 'valve_pulmonaire', 'gros_vaisseaux', 'pericarde', 'divers'
    ];

    categoryOrder.forEach(category => {
      const categoryCodes = codes[category] || [];
      // Sort codes by priority order before flattening
      const sortedCodes = sortCodesByPriority(categoryCodes, category);
      sortedCodes.forEach(codeObj => {
        flatCodes.push({
          code: codeObj.code,
          modifier: codeObj.modifier,
          value: codeObj.value,
          category: category
        });
      });
    });

    return flatCodes;
  }, [codes]);

  // Generate text file content
  const generateTextContent = useCallback(() => {
    const flatCodes = getAllCodesFlat();
    const codeMap = getCachedCodeMap();
    let content = '';

    flatCodes.forEach((codeObj, index) => {
      const hasValue = codeObj.value !== null && codeObj.value !== undefined;
      const hasModifier = !!codeObj.modifier;

      // Add the code itself
      content += codeObj.code + '\n';

      // Get code details to check if this code uses enum modifiers
      const codeDetails = codeMap.get(codeObj.code);

      // For codes with modifierEnumType, use modifier field to find position in enum
      // Generate DOWN sequences based on position - ignore value field entirely
      if (codeDetails?.modifierEnumType) {
        const modifiers = getModifiersForEnumSet(codeDetails.modifierEnumType);
        let modifierPosition = 0; // Default to first option

        if (hasModifier) {
          // Find position of modifier in the enum array
          modifierPosition = modifiers.findIndex(
            m => m.toLowerCase() === codeObj.modifier.toLowerCase()
          );
          if (modifierPosition < 0) modifierPosition = 0; // Fallback to first if not found
        }

        // Generate DOWN sequences based on position
        content += '[ENTER]\n';
        for (let i = 0; i < modifierPosition; i++) {
          content += '[DOWN]\n';
        }
        content += '[ENTER]\n';
        content += '[CLICK]\n';
      } else if (hasValue) {
        // Non-enum code with value
        // Format: code → [ENTER] → value → [ENTER] → [CLICK]
        content += '[ENTER]\n';
        content += codeObj.value + '\n';
        content += '[ENTER]\n';
        content += '[CLICK]\n';
      } else if (hasModifier) {
        // Non-enum code with modifier (legacy support)
        content += '[ENTER]\n';
        content += '[ENTER]\n';
        content += '[CLICK]\n';
      } else {
        // Case: No modifier, no value
        // Format: code → [ENTER] → [CLICK]
        content += '[ENTER]\n';
        content += '[CLICK]\n';
      }

      // Add blank line between codes for readability (except last one)
      if (index < flatCodes.length - 1) {
        content += '\n';
      }
    });

    return content;
  }, [getAllCodesFlat]);

  // Handle button click - directly copy to clipboard
  const handleClick = useCallback(() => {
    const flatCodes = getAllCodesFlat();
    if (flatCodes.length === 0) {
      return;
    }

    try {
      // Generate text content
      const textContent = generateTextContent();

      // Copy to clipboard
      navigator.clipboard.writeText(textContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Copy error:', err);
    }
  }, [getAllCodesFlat, generateTextContent]);

  // Don't render if in Electron (after all hooks)
  if (!isWeb) {
    return null;
  }

  return (
    <button
      className="echo-macro-button"
      onClick={handleClick}
      title="Copier les codes dans le presse-papier"
    >
      {copySuccess ? '✓ Copié !' : '📄 Exporter les codes'}
    </button>
  );
};

export default EchoCodesWebExportButton;
